import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import Button from "../Button/Button";

// Second confirmation for anything that cannot be undone. `tone` decides
// whether it reads as destructive or as an ordinary confirmation.
const TONES = {
  danger:  { chip: "bg-danger-50 text-danger-600",   button: "danger" },
  primary: { chip: "bg-brand-50 text-brand-600",     button: "primary" },
  warning: { chip: "bg-warning-50 text-warning-700", button: "warning" },
};

const ConfirmModal = ({
  title,
  message,
  onCancel,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "danger",
}) => {
  const modalRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const t = TONES[tone] ?? TONES.danger;

  useEffect(() => {
    confirmBtnRef.current?.focus();

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") onCancel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onCancel]);

  return (
    <AnimatePresence>
      {/* No backdrop blur: blurring a whole page of tables costs a frame or
          two on open, which reads as the dialog being slow to arrive. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.08, ease: "easeOut" }}
          className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-pop"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-start gap-3 px-5 pb-5 pt-5">
            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${t.chip}`}>
              <AlertTriangle size={17} strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold tracking-tight text-gray-900">{title}</h2>
              <p className="mt-1 text-[13px] leading-relaxed text-gray-500">{message}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3">
            <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
              {cancelText}
            </Button>
            <Button
              ref={confirmBtnRef}
              type="button"
              variant={t.button}
              size="sm"
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfirmModal;
